import { ThemeMode, useLightMode } from '@/hooks/useLightMode'
import { useTranslation } from 'react-i18next'
import { HiOutlineTrash } from 'react-icons/hi'

import { Box, Spacer } from '@chakra-ui/react'

import { Button as DarkButton, DarkProvider, TrashIcon } from '@hivewatch/dark'
import type { ColorScheme } from '@hivewatch/dark'

import { Button } from '@/components/ui'
import { formatBytes } from '@/utils/formats'

interface BorderStyleIProps {
  borderWidth: string
  borderColor: string
}

const getBorderStyle = (
  isInvalid: boolean,
  isDisabled: boolean,
  themeMode: ThemeMode,
  colorScheme?: ColorScheme
): BorderStyleIProps => {
  if (colorScheme) {
    if (isInvalid) {
      return {
        borderColor: `border.input-error.${colorScheme}`,
        borderWidth: '1px',
      }
    }
    if (isDisabled) {
      return { borderColor: `border.input.${colorScheme}`, borderWidth: '1px' }
    }
    return {
      borderColor: `border.input-hover.${colorScheme}`,
      borderWidth: '1px',
    }
  }

  // Pre-rebrand appearance, retained byte-for-byte for the callers that pass
  // no colorScheme. The two hexes have no faithful token — #D01030 exists
  // only as btn.offboard / incident.active, and #9FB8E5 not at all — so they
  // stay literal rather than minting tokens for a path that retires once
  // every caller is rebranded. The disabled case did map exactly:
  // border.default is #E2E8F0 in both modes, the same colour Chakra's banned
  // gray.200 resolves to.
  if (isInvalid) return { borderColor: '#D01030', borderWidth: '3px' }
  if (isDisabled) {
    return { borderColor: `border.default.${themeMode}`, borderWidth: '1px' }
  }
  return { borderColor: '#9FB8E5', borderWidth: '1px' }
}

interface FileUploadInputIProps {
  /**
   * Rebrand: renders the design-system button and trash icon instead of the
   * legacy ones, matching {@link FileAttachButton}. Omit to keep the legacy
   * appearance untouched.
   */
  colorScheme?: ColorScheme
  isInvalid: boolean
  isDisabled: boolean
  file: File
  onFileRemove: () => void
}

export const FileUploadInput = ({
  colorScheme,
  isInvalid,
  isDisabled,
  file,
  onFileRemove,
}: FileUploadInputIProps) => {
  const { t } = useTranslation()
  const mode = useLightMode()
  // An explicit colorScheme (rebrand call sites) overrides the app-wide mode.
  const themeMode = colorScheme ?? mode

  return (
    <Box
      alignItems='center'
      {...getBorderStyle(isInvalid, isDisabled, themeMode, colorScheme)}
      borderRadius={colorScheme ? '0' : 'var(--chakra-radii-md)'}
      borderStyle='solid'
      data-testid='FileUploadInput:container'
      display='flex'
      fontSize='14px'
      paddingLeft='16px'
    >
      <Box
        as='span'
        color={`text.primary.${themeMode}`}
        fontWeight='600'
        letterSpacing='-0.53px'
        maxW='45%'
        mr={3}
        noOfLines={1}
        opacity={isDisabled ? '0.2' : 'initial'}
      >
        {file?.name}
      </Box>
      <Box
        as='span'
        color={`text.secondary.${themeMode}`}
        fontWeight='500'
        letterSpacing='-0.53px'
        opacity={isDisabled ? '0.2' : 'initial'}
      >
        {formatBytes(file?.size)}
      </Box>
      <Spacer />
      {colorScheme ? (
        <DarkProvider colorScheme={colorScheme}>
          <DarkButton
            aria-label={t('common:remove')}
            colorScheme={colorScheme}
            disabled={isDisabled}
            leftIcon={<TrashIcon colorScheme={colorScheme} size={18} />}
            onClick={onFileRemove}
            variant='tertiary'
          >
            {t('common:remove')}
          </DarkButton>
        </DarkProvider>
      ) : (
        <Button
          isDisabled={isDisabled}
          leftIcon={<HiOutlineTrash size={24} />}
          onClick={onFileRemove}
          variant='secondary'
        >
          {t('common:remove')}
        </Button>
      )}
    </Box>
  )
}
